import { FormEvent, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Artikel, ArtikelInput, createArtikel, fetchArtikel, updateArtikel } from "@/lib/api"
import { SkeletonTabelle } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { useToast } from "@/components/Toast"
import { Button, FieldError, Input, Select, TableWrapper, TD_CLASS, TH_CLASS, TR_CLASS } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const LEER: ArtikelInput = { kuerzel: "", kurzbezeichnung: "" }

const ARTIKELTYP_LABELS: Record<string, string> = {
  rohstoff: "Rohstoff",
  fertigware: "Fertigware",
}

/** Artikelstamm-Übersicht + Anlegen/Bearbeiten im Sheet (Dokumente über Detailansicht später) — AP-2.7. */
export function ArtikelPage() {
  useEffect(() => {
    document.title = "Artikel — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const toast = useToast()
  const [suche, setSuche] = useState("")
  const [form, setForm] = useState<ArtikelInput>(LEER)
  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null)
  const [kuerzelFehler, setKuerzelFehler] = useState<string | null>(null)
  const [zeigeSheet, setZeigeSheet] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["artikel", suche],
    queryFn: () => fetchArtikel(suche || undefined),
  })

  const istEdit = bearbeiteId != null

  function oeffneNeu() {
    setBearbeiteId(null)
    setForm(LEER)
    setKuerzelFehler(null)
    setZeigeSheet(true)
  }

  function oeffneBearbeiten(a: Artikel) {
    setBearbeiteId(a.id)
    setForm({
      artikelnummer: a.artikelnummer,
      kuerzel: a.kuerzel,
      kurzbezeichnung: a.kurzbezeichnung,
      beschreibung: a.beschreibung,
      artikeltyp: a.artikeltyp,
      organisationId: a.organisationId,
    })
    setKuerzelFehler(null)
    setZeigeSheet(true)
  }

  const anlegen = useMutation({
    mutationFn: (payload: ArtikelInput) => createArtikel(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artikel"] })
      setForm(LEER)
      setZeigeSheet(false)
      toast.show("Artikel angelegt.", "success")
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.show(detail ?? "Artikel konnte nicht angelegt werden.", "error")
    },
  })

  const aktualisieren = useMutation({
    mutationFn: (payload: ArtikelInput) => updateArtikel(bearbeiteId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artikel"] })
      setZeigeSheet(false)
      toast.show("Artikel aktualisiert.", "success")
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.show(detail ?? "Artikel konnte nicht aktualisiert werden.", "error")
    },
  })

  const speichern = istEdit ? aktualisieren : anlegen

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.kuerzel?.trim()) {
      setKuerzelFehler("Kürzel ist erforderlich.")
      document.getElementById("artikel-kuerzel")?.focus()
      return
    }
    setKuerzelFehler(null)
    speichern.mutate(form)
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Artikel</h1>
          <p className="text-sm text-slate-600">
            Artikelstamm (Ninox `IB`) — Dokumente als Paperless-Verweis (AP-2.7).
          </p>
        </div>
        <Button onClick={oeffneNeu}>+ Neuer Artikel</Button>
      </header>

      <Sheet open={zeigeSheet} onOpenChange={setZeigeSheet}>
        <SheetContent sheetKey="artikel-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{istEdit ? "Artikel bearbeiten" : "Neuer Artikel"}</SheetTitle>
            <SheetDescription>
              {istEdit ? "Artikel im Artikelstamm bearbeiten." : "Artikel im Artikelstamm anlegen."}
            </SheetDescription>
          </SheetHeader>
          <form id="artikel-neu-form" onSubmit={onSubmit} className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="artikel-kuerzel" className="block text-xs font-medium text-slate-700">
                Kürzel *
              </label>
              <Input
                id="artikel-kuerzel"
                value={form.kuerzel ?? ""}
                onChange={(e) => {
                  setForm({ ...form, kuerzel: e.target.value })
                  if (kuerzelFehler) setKuerzelFehler(null)
                }}
                invalid={!!kuerzelFehler}
                aria-describedby={kuerzelFehler ? "artikel-kuerzel-fehler" : undefined}
              />
              <FieldError id="artikel-kuerzel-fehler">{kuerzelFehler}</FieldError>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Kurzbezeichnung</label>
              <Input
                value={form.kurzbezeichnung ?? ""}
                onChange={(e) => setForm({ ...form, kurzbezeichnung: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Artikeltyp</label>
              <Select
                value={form.artikeltyp ?? ""}
                onChange={(e) => setForm({ ...form, artikeltyp: e.target.value || null })}
              >
                <option value="">—</option>
                <option value="rohstoff">Rohstoff</option>
                <option value="fertigware">Fertigware</option>
              </Select>
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeSheet(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="artikel-neu-form" disabled={speichern.isPending}>
              {speichern.isPending ? "Speichert…" : istEdit ? "Speichern" : "Anlegen"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Input
        type="text"
        placeholder="Suche nach Kürzel/Kurzbezeichnung…"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        className="mb-4 w-64"
      />

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Artikel konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_CLASS}>Kürzel</th>
              <th className={TH_CLASS}>Kurzbezeichnung</th>
              <th className={TH_CLASS}>Typ</th>
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonTabelle zeilen={5} spalten={3} />
          ) : (
            <tbody>
              {(data ?? []).map((a) => (
                <tr
                  key={a.id}
                  className={`${TR_CLASS} cursor-pointer`}
                  onClick={() => oeffneBearbeiten(a)}
                >
                  <td className={`${TD_CLASS} font-mono`}>{a.kuerzel ?? "—"}</td>
                  <td className={`${TD_CLASS} text-slate-900`}>{a.kurzbezeichnung ?? "—"}</td>
                  <td className={TD_CLASS}>
                    {a.artikeltyp ? ARTIKELTYP_LABELS[a.artikeltyp] ?? a.artikeltyp : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableWrapper>

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState text={suche ? "Keine Artikel gefunden." : "Noch keine Artikel vorhanden."} />
      )}
    </div>
  )
}
